import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaBell, FaCheckCircle, FaClock, FaTimesCircle, FaTruck, FaRegCircle, FaSyncAlt } from 'react-icons/fa';
import { io } from 'socket.io-client';
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function NotificationsPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterType, setFilterType] = useState('all'); // 'all', 'new', 'cancelled', 'delivered'

  const mapOrderToActivity = (item) => {
    let type = 'info';
    let icon = <FaRegCircle />;
    let title = item.title || 'Order Update';
    let badgeColor = '#1a237e';
    let badgeBg = '#e8eaf6';

    if (item.type === 'new_order') {
      type = 'new';
      icon = <FaClock style={{ color: '#d97706' }} />;
      badgeColor = '#b45309';
      badgeBg = '#fef3c7';
    } else if (item.type === 'payment_received') {
      type = 'payment';
      icon = <FaCheckCircle style={{ color: '#16a34a' }} />;
      badgeColor = '#15803d';
      badgeBg = '#dcfce7';
    } else if (item.type === 'order_cancelled') {
      type = 'cancelled';
      icon = <FaTimesCircle style={{ color: '#dc2626' }} />;
      badgeColor = '#b91c1c';
      badgeBg = '#fee2e2';
    } else {
      if (item.title?.includes('Delivered')) {
        type = 'delivered';
        icon = <FaCheckCircle style={{ color: '#16a34a' }} />;
        badgeColor = '#15803d';
        badgeBg = '#dcfce7';
      } else if (item.title?.includes('Shipped')) {
        type = 'shipped';
        icon = <FaTruck style={{ color: '#0d9488' }} />;
        badgeColor = '#0f766e';
        badgeBg = '#ccfbf1';
      } else {
        type = 'confirmed';
        icon = <FaCheckCircle style={{ color: '#1a237e' }} />;
        badgeColor = '#1a237e';
        badgeBg = '#e8eaf6';
      }
    }

    let paymentBadgeColor = '#b45309';
    let paymentBadgeBg = '#fef3c7';
    if (item.paymentStatusLabel === 'PAID') {
      paymentBadgeColor = '#15803d';
      paymentBadgeBg = '#dcfce7';
    } else if (item.paymentStatusLabel === 'FAILED') {
      paymentBadgeColor = '#b91c1c';
      paymentBadgeBg = '#fee2e2';
    }

    return {
      id: item.orderId || item._id,
      orderNumber: item.orderNumber,
      title,
      message: item.message,
      time: item.createdAt,
      type,
      icon,
      badgeColor,
      badgeBg,
      statusLabel: item.statusLabel || 'PENDING',
      itemsCount: 0,
      paymentStatusLabel: item.paymentStatusLabel || 'PENDING',
      paymentBadgeColor,
      paymentBadgeBg
    };
  };

  const fetchActivities = async (pageNumber = 1, isInitial = false, customFilter = filterType) => {
    if (pageNumber === 1 || isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pageNumber,
          limit: 100,
          type: customFilter
        }
      });

      const { activities: fetchedOrders, total, pages } = response.data;

      // Map orders to Activity items
      const mappedActivities = fetchedOrders.map(mapOrderToActivity);

      setActivities(prev => {
        const combined = pageNumber === 1 ? mappedActivities : [...prev, ...mappedActivities];
        // Filter out duplicate IDs to handle socket integrations safely
        return combined.filter((item, index, self) => self.findIndex(t => t.id === item.id) === index);
      });

      setPage(pageNumber);
      setHasMore(pageNumber < pages);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loaderRef = useRef(null);

  useEffect(() => {
    fetchActivities(1, true);
  }, []);

  useEffect(() => {
    // Connect to WebSocket server
    const socket = io(API_BASE_URL);

    // Listen for new orders to prepend
    socket.on('new-order', (newOrder) => {
      console.log('[Socket.io] Activities page received new-order:', newOrder);
      const activity = mapOrderToActivity(newOrder);
      setActivities(prev => [activity, ...prev].filter((item, index, self) => self.findIndex(t => t.id === item.id) === index));
    });

    // Listen for order status updates
    socket.on('order-updated', (updatedOrder) => {
      console.log('[Socket.io] Activities page received order-updated:', updatedOrder);
      const activity = mapOrderToActivity(updatedOrder);
      setActivities(prev => prev.map(item => item.id === updatedOrder._id ? activity : item));
    });

    // Listen for order online payment successes
    socket.on('order-paid-online', (paidOrder) => {
      console.log('[Socket.io] Activities page received order-paid-online:', paidOrder);
      const activity = mapOrderToActivity(paidOrder);
      setActivities(prev => prev.map(item => item.id === paidOrder._id ? activity : item));
    });

    // Listen for order cancellations
    socket.on('order-cancelled', (cancelledOrder) => {
      console.log('[Socket.io] Activities page received order-cancelled:', cancelledOrder);
      const activity = mapOrderToActivity(cancelledOrder);
      setActivities(prev => prev.map(item => item.id === cancelledOrder._id ? activity : item));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchActivities(page + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, loadingMore, loading, page]);

  const handleFilterChange = (type) => {
    setFilterType(type);
    setPage(1);
    setHasMore(true);
    setActivities([]);
    fetchActivities(1, true, type);
  };



  return (
    <div className="container-fluid p-3 pt-2 g-0 adma" style={{ minHeight: '85vh' }}>
      <div className="row mb-0 align-items-center">
        <div className="col-12 col-md-8">
          <div className="section-title">
            <h4 className="mt-1">Recent Activities & Alerts</h4>
            <p className="text-muted p-0 m-0">Real-time audit log of customer checkouts, cancellations, and order updates</p>
          </div>
        </div>
        <div className="col-12 col-md-4 text-md-end mt-3 mt-md-0">
          <button 
            title="Refresh Activities"
            onClick={() => fetchActivities(1, true)}
            style={{ 
              backgroundColor: '#1a237e', 
              color: '#ffffff', 
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease-in-out'
            }}
            onMouseOver={e => { e.currentTarget.style.transform = 'rotate(180deg) scale(1.08)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'rotate(0deg) scale(1)'; }}
          >
            <FaSyncAlt size={14} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="d-flex align-items-center gap-1 mt-0 mb-0" style={{ borderBottom: '1px solid #d1d5db', paddingTop: '8px', paddingBottom: '8px', paddingLeft: '12px', backgroundColor: '#ffffff' }}>
        {[
          { id: 'all', label: 'All Activities' },
          { id: 'new', label: 'New Orders' },
          { id: 'cancelled', label: 'Cancellations' },
          { id: 'delivered', label: 'Deliveries' }
        ].map(tab => {
          const isActive = filterType === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleFilterChange(tab.id)}
              style={{
                border: 'none',
                background: isActive ? '#e8eaf6' : 'transparent',
                color: isActive ? '#1a237e' : '#475569',
                fontWeight: isActive ? '700' : '600',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                textTransform: 'uppercase'
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Activity Timeline List - Edge to Edge */}
      <div className="row orders-table-row">
        <div className="col-12">
          {loading ? (
            <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', borderBottom: '1px solid #d1d5db' }}>
              <AdminPageLoader message="Loading activity history..." />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center bg-white border p-5" style={{ borderBottom: '1px solid #d1d5db' }}>
              <FaBell size={36} className="text-muted mb-3" />
              <h6 className="fw-semibold text-dark">No recent notifications matching filter</h6>
              <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Activities will populate automatically when checkouts or updates occur.</p>
            </div>
          ) : (
            <div style={{ backgroundColor: '#ffffff', borderTop: 'none', borderBottom: '1px solid #d1d5db' }}>
              {activities.map((activity, index) => (
                <div 
                  key={`${activity.id}-${index}`} 
                  className="d-flex align-items-center gap-3 border-bottom"
                  style={{ 
                    padding: '16px 20px',
                    transition: 'background-color 0.15s',
                    backgroundColor: '#ffffff'
                  }}
                  onMouseOver={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                  onMouseOut={e => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                >
                  <div 
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      backgroundColor: activity.badgeBg, 
                      flexShrink: 0,
                      fontSize: '16px'
                    }}
                  >
                    {activity.icon}
                  </div>
                  <div className="flex-grow-1">
                    <div className="mb-1">
                      <h6 className="fw-bold mb-0" style={{ fontSize: '14.5px', color: '#0f172a' }}>
                        {activity.title}
                      </h6>
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <span 
                        className="badge" 
                        style={{ 
                          backgroundColor: activity.badgeBg, 
                          color: activity.badgeColor,
                          fontSize: '10px',
                          fontWeight: '700',
                          padding: '4px 8px'
                        }}
                      >
                        {activity.statusLabel}
                      </span>
                      <span 
                        className="badge" 
                        style={{ 
                          backgroundColor: activity.paymentBadgeBg, 
                          color: activity.paymentBadgeColor,
                          fontSize: '10px',
                          fontWeight: '700',
                          padding: '4px 8px'
                        }}
                      >
                        {activity.paymentStatusLabel}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        Order: <b>{activity.orderNumber}</b> ({activity.itemsCount} items)
                      </span>
                    </div>
                    <p className="mb-0 text-secondary" style={{ fontSize: '13px' }}>
                      {activity.message}
                    </p>
                  </div>
                  <div style={{ flexShrink: 0, color: '#64748b', fontSize: '11.5px', marginRight: '16px' }}>
                    {new Date(activity.time).toLocaleString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Link
                      to={`/admin/orders-details/${activity.id}`}
                      className="btn btn-sm btn-outline-primary"
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        borderColor: '#1a237e',
                        color: '#1a237e',
                        borderRadius: '4px',
                        padding: '4px 10px'
                      }}
                      onMouseOver={e => { e.currentTarget.style.backgroundColor = '#1a237e'; e.currentTarget.style.color = '#ffffff'; }}
                      onMouseOut={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#1a237e'; }}
                    >
                      View Order
                    </Link>
                  </div>
                </div>
              ))}

              {/* Infinite Scroll Trigger */}
              <div ref={loaderRef} style={{ padding: '24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '1px solid #f1f5f9' }}>
                {loadingMore ? (
                  <div style={{ color: '#1a237e', fontSize: '13px', fontWeight: '600' }}>
                    Loading more notifications...
                  </div>
                ) : !hasMore && activities.length > 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: '500' }}>
                    No more notifications
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NotificationsPage;
